import { useEffect, useState } from 'react';
import AdminTabs from '@/features/admin/components/AdminTabs';
import { Card, CardHeader } from '@/shared/ui/Card';
import { Pill, type PillTone } from '@/shared/ui/Pill';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { ApiError } from '@/shared/api/apiClient';
import { safeErrorMessage } from '@/shared/utils/errors';
import {
  getAvailability,
  getCostDelta,
  getErrors,
  getEstimateLatency,
  getGpuQueue,
  getGuardrailRate,
  getQpm,
  getRegenerationRate,
  type AvailabilityReport,
  type CostDeltaReport,
  type ErrorsReport,
  type EstimateLatencyReport,
  type GpuQueueReport,
  type GuardrailRateReport,
  type QpmReport,
  type RegenerationRateReport,
} from '@/features/admin/services/sliService';

const WINDOW_OPTIONS = [
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: '24 hours', minutes: 1440 },
  { label: '7 days', minutes: 10080 },
];

// QPM reflects current load, not a long-run average — cap it well below whatever
// the page-wide window is so a "7 days" selection doesn't average QPM over a week.
const QPM_MAX_WINDOW_MINUTES = 15;
const REFRESH_INTERVAL_MS = 30_000;

function formatNumber(value: number | null | undefined, digits = 0): string {
  return value === null || value === undefined ? '—' : value.toFixed(digits);
}

// Higher-is-better metric (e.g. availability): red below failAt, warn below warnAt.
// Pass higherIsBetter=false for a rate where LOWER is healthy (e.g. an error rate).
function healthTone(pct: number | null, warnAt: number, failAt: number, higherIsBetter: boolean): PillTone {
  if (pct === null) return 'neutral';
  const bad = higherIsBetter ? pct < failAt : pct > failAt;
  if (bad) return 'danger';
  const warn = higherIsBetter ? pct < warnAt : pct > warnAt;
  return warn ? 'warn' : 'green';
}

function PctPill({
  pct,
  warnAt,
  failAt,
  higherIsBetter = true,
}: {
  pct: number | null;
  warnAt: number;
  failAt: number;
  higherIsBetter?: boolean;
}) {
  return (
    <Pill tone={healthTone(pct, warnAt, failAt, higherIsBetter)} dot>
      {pct === null ? 'n/a' : `${pct.toFixed(1)}%`}
    </Pill>
  );
}

type SliData = {
  availability: AvailabilityReport;
  latency: EstimateLatencyReport;
  gpuQueue: GpuQueueReport;
  qpm: QpmReport;
  // null specifically means valoi-billing was unreachable (a 502 from the engine),
  // distinct from "reachable, zero settled holds in this window".
  costDelta: CostDeltaReport | null;
  errors: ErrorsReport;
  guardrail: GuardrailRateReport;
  regeneration: RegenerationRateReport;
};

export default function AdminSliPage() {
  const [windowMinutes, setWindowMinutes] = useState(60);
  const [data, setData] = useState<SliData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const qpmMinutes = Math.min(windowMinutes, QPM_MAX_WINDOW_MINUTES);
      try {
        const [availability, latency, gpuQueue, qpm, costDelta, errors, guardrail, regeneration] = await Promise.all([
          getAvailability(windowMinutes),
          getEstimateLatency(windowMinutes),
          getGpuQueue(windowMinutes),
          getQpm(qpmMinutes),
          // valoi-billing being down shouldn't blank the whole dashboard — every
          // other card is sourced entirely from valoi_engine's own DB. But only
          // swallow the specific 502 the engine returns for "couldn't reach
          // valoi-billing" (see admin_api.py::sli_cost_delta) — an auth/500 bug in
          // this endpoint itself must still surface as a real error, not get
          // mislabeled as "valoi-billing unavailable".
          getCostDelta(windowMinutes).catch((err: unknown) => {
            if (err instanceof ApiError && err.status === 502) return null;
            throw err;
          }),
          getErrors(windowMinutes),
          getGuardrailRate(windowMinutes),
          getRegenerationRate(windowMinutes),
        ]);
        if (cancelled) return;
        setData({ availability, latency, gpuQueue, qpm, costDelta, errors, guardrail, regeneration });
        setError(null);
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        setError(safeErrorMessage(err, 'Unable to load service health data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [windowMinutes]);

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-header">
        <h1>Service Health</h1>
        <p>Internal SLI dashboard — auto-refreshes every 30s.</p>
      </div>

      <div className="sli-toolbar">
        <label htmlFor="sli-window">Window</label>
        <select id="sli-window" value={windowMinutes} onChange={(e) => setWindowMinutes(Number(e.target.value))}>
          {WINDOW_OPTIONS.map((opt) => (
            <option key={opt.minutes} value={opt.minutes}>
              {opt.label}
            </option>
          ))}
        </select>
        {lastUpdated && <span className="sli-toolbar__updated">Updated {lastUpdated.toLocaleTimeString()}</span>}
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {loading && !data ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : (
        data && (
          <div className="sli-grid">
            <Card>
              <CardHeader title="Availability" description="Per-provider success rate" />
              {data.availability.providers.length === 0 ? (
                <p className="sli-empty">No calls in window</p>
              ) : (
                <ul className="sli-list">
                  {data.availability.providers.map((p) => (
                    <li key={p.provider} className="sli-row">
                      <span>{p.provider}</span>
                      <span className="sli-row__value">
                        <PctPill pct={p.availabilityPct} warnAt={99} failAt={95} />
                        <span className="sli-row__meta">
                          {p.successful}/{p.total}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title="Cost-estimate latency" description="credits.estimate_rough duration" />
              {data.latency.count === 0 ? (
                <p className="sli-empty">No samples in window</p>
              ) : (
                <ul className="sli-list">
                  <li className="sli-row">
                    <span>p50</span>
                    <span className="sli-row__value">{formatNumber(data.latency.p50Ms)} ms</span>
                  </li>
                  <li className="sli-row">
                    <span>p95</span>
                    <span className="sli-row__value">{formatNumber(data.latency.p95Ms)} ms</span>
                  </li>
                  <li className="sli-row">
                    <span>avg</span>
                    <span className="sli-row__value">{formatNumber(data.latency.avgMs)} ms</span>
                  </li>
                  <li className="sli-row sli-row--meta">
                    <span>samples</span>
                    <span className="sli-row__value">{data.latency.count}</span>
                  </li>
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title="GPU queue (RunPod)" description="Wan endpoint queue depth" />
              <ul className="sli-list">
                {data.gpuQueue.endpoints.length === 0 ? (
                  <li className="sli-empty">No endpoint samples yet</li>
                ) : (
                  data.gpuQueue.endpoints.map((e) => (
                    <li key={e.endpointId} className="sli-row">
                      <span>{e.endpointId}</span>
                      <span className="sli-row__value">
                        {e.inQueue} queued / {e.inProgress} running
                      </span>
                    </li>
                  ))
                )}
                <li className="sli-row sli-row--meta">
                  <span>avg dispatch wait</span>
                  <span className="sli-row__value">
                    {formatNumber(data.gpuQueue.avgWaitSeconds, 1)}s (n={data.gpuQueue.waitSampleCount})
                  </span>
                </li>
              </ul>
            </Card>

            <Card>
              <CardHeader title="Queries / minute" description="Gemini, ElevenLabs, ..." />
              {data.qpm.providers.length === 0 ? (
                <p className="sli-empty">No calls in window</p>
              ) : (
                <ul className="sli-list">
                  {data.qpm.providers.map((p) => (
                    <li key={p.provider} className="sli-row">
                      <span>{p.provider}</span>
                      <span className="sli-row__value">{formatNumber(p.qpm, 2)} / min</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title="Estimate vs. actual cost" description="Settled holds, valoi-billing" />
              {data.costDelta === null ? (
                <p className="sli-empty">valoi-billing unavailable</p>
              ) : data.costDelta.count === 0 ? (
                <p className="sli-empty">No settled holds in window</p>
              ) : (
                <ul className="sli-list">
                  <li className="sli-row">
                    <span>avg delta</span>
                    <span className="sli-row__value">{formatNumber(data.costDelta.avgDeltaPct, 1)}%</span>
                  </li>
                  <li className="sli-row">
                    <span>p50</span>
                    <span className="sli-row__value">{formatNumber(data.costDelta.p50DeltaPct, 1)}%</span>
                  </li>
                  <li className="sli-row">
                    <span>p95</span>
                    <span className="sli-row__value">{formatNumber(data.costDelta.p95DeltaPct, 1)}%</span>
                  </li>
                  <li className="sli-row sli-row--meta">
                    <span>settled holds</span>
                    <span className="sli-row__value">{data.costDelta.count}</span>
                  </li>
                </ul>
              )}
            </Card>

            <Card>
              <CardHeader title="Content guardrail rate" description="Scenes rejected by content-safety" />
              {data.guardrail.totalScenes === 0 ? (
                <p className="sli-empty">No scenes in window</p>
              ) : (
                <>
                  <div className="sli-big">
                    <PctPill pct={data.guardrail.guardrailPct} warnAt={10} failAt={25} higherIsBetter={false} />
                  </div>
                  <p className="sli-row__meta">
                    {data.guardrail.guardrailTriggered}/{data.guardrail.totalScenes} scenes
                  </p>
                </>
              )}
            </Card>

            <Card>
              <CardHeader title="Scene regeneration rate" description="Any retry reason" />
              {data.regeneration.totalScenes === 0 ? (
                <p className="sli-empty">No scenes in window</p>
              ) : (
                <>
                  <div className="sli-big">
                    <PctPill pct={data.regeneration.regenerationPct} warnAt={10} failAt={25} higherIsBetter={false} />
                  </div>
                  <p className="sli-row__meta">
                    {data.regeneration.regeneratedScenes}/{data.regeneration.totalScenes} scenes ·{' '}
                    {formatNumber(data.regeneration.avgRetriesPerJob, 2)} avg retries/job
                  </p>
                </>
              )}
            </Card>

            <Card>
              <CardHeader title="Service errors" description="By provider, job category, exception type" />
              {data.errors.byProvider.length === 0 &&
              data.errors.byJobErrorCategory.length === 0 &&
              data.errors.byExcType.length === 0 ? (
                <p className="sli-empty">No errors in window</p>
              ) : (
                <ul className="sli-list">
                  {data.errors.byProvider.map((r) => (
                    <li key={`provider-${r.provider}`} className="sli-row">
                      <span>{r.provider} (failed calls)</span>
                      <span className="sli-row__value">{r.failedCalls}</span>
                    </li>
                  ))}
                  {data.errors.byJobErrorCategory.map((r) => (
                    <li key={`job-${r.category}`} className="sli-row">
                      <span>{r.category} (job errors)</span>
                      <span className="sli-row__value">{r.count}</span>
                    </li>
                  ))}
                  {data.errors.byExcType.slice(0, 5).map((r) => (
                    <li key={`exc-${r.excType}`} className="sli-row">
                      <span>{r.excType} (500s)</span>
                      <span className="sli-row__value">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )
      )}
    </section>
  );
}
